<template>
  <div>
    <div class="d-flex justify-content-end">
      <div class="d-flex justify-content-between button-group">
        <img v-if="currentRoutePermissions.add_purchasecontract"
             src="@/assets/images/common-icons/icon_create.svg" @click="createPriceList"
             :title="$t('buttons.contracts.create_contract')">
        <img v-if="currentRoutePermissions.delete_purchasecontract"
             src="@/assets/images/common-icons/icon_delete.svg" @click="deletePriceList"
             :title="$t('buttons.contracts.delete_contract')">
        <img src="@/assets/images/common-icons/icon_clear.png" @click="cleanFilters"
             :title="$t('buttons.common.clear_filters')">
        <img src="@/assets/images/common-icons/icon_search.svg" @click="search"
             :title="$t('buttons.common.search')">
      </div>
    </div>
    <div class="search-form">
      <div class="search-form__filters">
        <b-row>
          <b-col>
            <label>{{ $t('labels.price_list.date') }}</label>
            <range-date-picker v-model="searchData.date_start_between" range lang="en"
                               placeholder=""
                               range-separator="/" :first-day-of-week="1"></range-date-picker>
          </b-col>
          <b-col>
            <label>{{ $t('labels.common.status') }}</label>
            <multiselect v-model="searchData.status" :max-height="200"
                         placeholder=""
                         :options="contractStatuses"
                         :taggable="true" :close-on-select="true"></multiselect>
          </b-col>
          <b-col>
            <label>{{ $t('labels.common.currency') }}</label>
            <multiselect v-model="searchData.currency" :max-height="200"
                         placeholder="" label="code" track-by="code"
                         :options="currencies" :multiple="true"
                         :taggable="true" :close-on-select="false"></multiselect>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <label>{{ $t('labels.price_list.price_list_number') }}</label>
            <input class="form-control" v-model="searchData.price_list_nr" @keyup.enter="search"
                   placeholder="">
          </b-col>
          <b-col>
            <label>{{ $t('labels.price_list.expiration_date') }}</label>
            <range-date-picker v-model="searchData.delivery_term_between" range lang="en"
                               placeholder=""
                               range-separator="/" :first-day-of-week="1"></range-date-picker>
          </b-col>
          <b-col>
            <label>{{ $t('labels.common.creator') }}</label>
            <multiselect v-model="searchData.created_by" :max-height="200"
                         placeholder="" label="username" track-by="id"
                         :options="users" :multiple="true"
                         :taggable="true" :close-on-select="false"></multiselect>
          </b-col>
        </b-row>
        <div>
          <b-row>
            <b-col cols="4">
              <input v-model="searchData.filter" class="form-control"
                     placeholder=""
                     @input="setQuickFilter">
            </b-col>
            <b-col cols="4" offset="4" class="vertical-center-container d-flex justify-content-end">
              <label for="pagination-toggle" class="margin-right-5">{{$t('labels.common.pagination')}}</label>
              <input id="pagination-toggle" type="checkbox"
                     title="this checkbox needed for toggle pagination bar" v-model="paginationState"
                     @change="togglePagination">
              <save-columns-btn :grid="gridOptions" :grid-name="gridName" class="margin-left-5"></save-columns-btn>
            </b-col>
          </b-row>
        </div>
        <div>
          <b-row>
            <b-col>
              <ag-grid-vue class="ag-theme-balham grid" :gridOptions="gridOptions"
                           :rowDoubleClicked="goToPriceList" :pinnedBottomRowData="pinnedRowData"
                           :columnDefs="columns" :rowData="contracts" :postProcessPopup="postProcessPopup">
              </ag-grid-vue>
            </b-col>
          </b-row>
          <b-row class="pagination__wrapper" v-if="paginationState">
            <b-col cols="5">
              <div class="pagination-info">{{ countOfViewPriceList }} {{ $t('common_content.of') }} {{ contractsCount }}
              </div>
            </b-col>
            <b-col cols="7">
              <b-pagination size="md"
                            :total-rows="contractsCount"
                            :per-page="searchObject.limit"
                            v-model="currentPaginateIndex"
                            @input="changePaginateState"
              ></b-pagination>
            </b-col>
          </b-row>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue'
import {
  mapGetters,
  mapMutations,
  mapState
} from 'vuex'
import Multiselect from 'vue-multiselect'
import RangeDatePicker from 'vue2-datepicker'
import moment from 'moment'
import SaveColumnsBtn from '@/components/common/SaveColumnsBtn'
import {
  cleanFilters,
  saveFilters,
  setFilters,
  checkSavedFilterData,
  getArrayByProperty,
  setNotifyData
} from '@/helpers/commonHelpers'
import { notificationsRenderer } from '@/helpers/renderHelpers'
import { gridMixins } from '@/mixins'
import { DATE_FORMAT } from '@/constants'
import { translateColumnsProperly } from '@/helpers/translate-helper'

export default {
  mixins: [gridMixins],
  components: {
    Multiselect,
    RangeDatePicker,
    SaveColumnsBtn,
    'ag-grid-vue': AgGridVue
  },
  data () {
    return {
      searchData: {
        currency: [],
        filter: '',
        date_start_between: null,
        delivery_term_between: null,
        price_list_nr: '',
        range_date: null,
        delivery_term: null,
        status: 'active',
        created_by: []
      },
      searchObject: {
        limit: 15,
        offset: 0
      },
      gridApi: null,
      paginationState: true,
      currentPaginateIndex: 1
    }
  },
  methods: {
    ...mapMutations('priceLists', ['setColumns']),
    translateColumnsProperly,
    async search () {
      this.currentPaginateIndex = 1
      const { translateColumnsProperly } = this
      this.searchObject = {
        ...this.searchObject,
        ...this.searchData,
        date_start_between_after: this.searchData.date_start_between && this.searchData.date_start_between[0]
            ? moment(this.searchData.date_start_between[0]).format(DATE_FORMAT) : null,
        date_start_between_before: this.searchData.date_start_between && this.searchData.date_start_between[1]
            ? moment(this.searchData.date_start_between[1]).format(DATE_FORMAT) : null,
        currency: getArrayByProperty(this.searchData.currency, 'id').toString(),
        created_by: getArrayByProperty(this.searchData.created_by, 'id').toString(),
        // status: this.searchData.status,
        delivery_term_between_after: this.searchData.delivery_term_between && this.searchData.delivery_term_between[0]
            ? moment(this.searchData.delivery_term_between[0]).format(DATE_FORMAT) : null,
        delivery_term_between_before: this.searchData.delivery_term_between && this.searchData.delivery_term_between[1]
            ? moment(this.searchData.delivery_term_between[1]).format(DATE_FORMAT) : null,
        translateColumnsProperly
      }
      await this.$store.dispatch('priceLists/loadPriceList', this.searchObject)
      saveFilters('purchasePriceList', this.searchData)
    },
    createPriceList () {
      this.$router.push({ path: `/price-list/new` })
    },
    deletePriceList () {
      const id = getArrayByProperty(this.gridOptions.api.getSelectedRows(), 'id')
      if (id.length) {
        this.$http.delete(`purchase/price_lists/`, { body: { id } }).then((response) => {
          this.$notify(setNotifyData('Success', response.body, 'success'))
          this.currentPaginateIndex = 1
          this.$store.dispatch('priceLists/loadPriceList', this.searchObject)
        }).catch((error) => {
          console.log(error)
          notificationsRenderer(error.body, 'error')
        })
      } else {
        this.$notify(setNotifyData('Select price list for removing', '', 'warn'))
      }
    },
    goToPriceList (params) {
      if (params.data && params.data.id) {
        this.$router.push({ path: `/price-list/${params.data.id}` })
      }
    },
    cleanFilters () {
      cleanFilters(this.searchData)
    },
    setQuickFilter () {
      this.gridOptions.api.setQuickFilter(this.searchData.filter)
      saveFilters('purchaseContracts', this.searchData)
    },
    changePaginateState () {
      const searchObject = { ...this.searchObject, offset: (this.currentPaginateIndex - 1) * this.searchObject.limit }
      this.$store.dispatch('priceLists/loadPriceList', searchObject)
    },
    togglePagination () {
      if (!this.paginationState) {
        delete this.searchObject.limit
        delete this.searchObject.offset
      } else {
        this.searchObject.limit = 15
        this.searchObject.offset = 0
      }
      this.search()
    },
    clearData () {
      this.$store.commit('priceLists/setData', [])
    }
  },
  async mounted () {
    await this.$store.cache.dispatch('woodware/getUsers')
    await this.$store.cache.dispatch('generic/getCurrencies')
    if (checkSavedFilterData('purchasePriceList')) {
      this.searchData = setFilters('purchasePriceList')
      this.setQuickFilter()
    }
    await this.search()
    await this.$nextTick()
    // const translatedColumns = translateColumns.call(this, this.columns)
    // console.log(translatedColumns, '123qwe')
    // this.gridApi = this.gridOptions.api
    // this.gridApi.setColumnDefs([])
    // this.gridApi.refreshCells({ force: true })
    // await this.setColumns(translatedColumns)
  },
  beforeRouteEnter (to, from, next) {
    next(vm => {
      vm.clearData()
    })
  },
  computed: {
    ...mapState('priceLists', ['gridName']),
    ...mapState('generic', ['currencies', 'currentRoutePermissions']),
    ...mapGetters('priceLists', {
      gridOptions: 'gridOptions'
    }),
    ...mapState('priceLists', ['contracts', 'columns', 'contractsCount', 'contractStatuses', 'pinnedRowData']),
    ...mapState('woodware', ['users']),
    countOfViewPriceList () {
      return this.contracts.length
    }
  }
}
</script>

<style scoped>

.grid {
  height: calc(100vh - 400px);
}

</style>



// WEBPACK FOOTER //
// src/components/pages/modules/purchases/price-list/PurchasePriceList.vue